import { EditOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { useMutation, useSuspenseQuery } from '@apollo/client'
import {
  DeletePolicyUserRoleDocument,
  FetchPolicyUserRolesDocument,
  FetchUsersDocument,
  type PolicyUserRoleFieldsFragment,
} from '@salescore/client-api'
import { getOrganizationIdFromPath, useCanForFeatures } from '@salescore/client-common'
// eslint-disable-next-line sonarjs/deprecation
import { createDestroyColumn, datetimeFormat, useModal } from '@salescore/frontend-common'
import { Alert, Button, Table } from 'antd'
import { t } from 'i18next'
import { type ReactNode, useState } from 'react'

import { PolicyCreateModal } from './PolicyCreateModal'
import { PolicyEditModal } from './PolicyEditModal'

// デフォルトポリシーは DB の行ではないので更新日時を持たない
type PolicyRow = Omit<PolicyUserRoleFieldsFragment, 'updatedAt' | 'createdAt'> & { updatedAt: string | null }

// eslint-disable-next-line sonarjs/todo-tag
// TODO: 安定稼働確認後デフォルトポリシーもBE(PolicyUserRoleテーブル)から引くようにする
const defaultPolicies: PolicyRow[] = [t(`一般ユーザー`), t(`現場マネージャー`), t(`組織管理者`)].map(
  (label, index) => ({
    id: `default-${index}`,
    name: '',
    label,
    description: t(`システムで用意されているロールです。`),
    updatedAt: null,
    updatedById: null,
  }),
)

const isCustomPolicyRow = (row: PolicyRow): row is PolicyUserRoleFieldsFragment => !row.id.startsWith('default-')

export const PoliciesTable = (): ReactNode => {
  const { canManagePolicy } = useCanForFeatures()
  const {
    data: { policyUserRoles },
  } = useSuspenseQuery(FetchPolicyUserRolesDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })

  if (!canManagePolicy) {
    return (
      <div>
        <Alert type="warning" message={t(`権限がありません`)} />
      </div>
    )
  }

  return <Body policyUserRoles={policyUserRoles} />
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function Body({ policyUserRoles }: { policyUserRoles: PolicyUserRoleFieldsFragment[] }): ReactNode {
  const { data: users } = useSuspenseQuery(FetchUsersDocument, {
    variables: { organizationId: getOrganizationIdFromPath() },
  })
  const [loading, setLoading] = useState(false)
  const [deletePolicyUserRoleMutation] = useMutation(DeletePolicyUserRoleDocument)

  // eslint-disable-next-line sonarjs/deprecation
  const createPolicyModal = useModal()
  // eslint-disable-next-line sonarjs/deprecation
  const editPolicyModal = useModal<{ policyUserRole: PolicyUserRoleFieldsFragment }>()

  return (
    <>
      <PageHeader
        title={t(`権限セット一覧`)}
        extra={[
          <Button
            key="button-create-policy"
            type="primary"
            onClick={() => {
              createPolicyModal.showModal()
            }}
          >
            {t(`新規作成`)}
          </Button>,
        ]}
      >
        <Table
          rowKey="id"
          size="small"
          pagination={{ defaultPageSize: 100 }}
          dataSource={[...defaultPolicies, ...policyUserRoles]}
          columns={[
            {
              title: t(`権限セット名`),
              dataIndex: 'label',
              width: 200,
              render(_text, record): ReactNode {
                return <span>{record.label}</span>
              },
            },
            {
              title: t(`説明`),
              dataIndex: 'description',
              render(_text, record): ReactNode {
                return <span>{record.description}</span>
              },
            },
            {
              title: t(`最終更新日`),
              dataIndex: 'updatedAt',
              width: 200,
              render(_text, record): ReactNode {
                if (record.updatedAt === null) {
                  return <span>-</span>
                }
                return <span>{datetimeFormat(record.updatedAt)}</span>
              },
            },
            {
              title: t(`最終更新ユーザー`),
              width: 200,
              dataIndex: 'updatedBy',
              render(_, record): ReactNode {
                if (record.updatedById === undefined || record.updatedById === null) {
                  return <span>-</span>
                }
                const user = users.users.find((x) => x.id === record.updatedById)
                return <span>{user?.name}</span>
              },
            },
            {
              title: ``,
              key: `edit`,
              width: 150,
              render(_, record): ReactNode {
                return (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      if (isCustomPolicyRow(record)) {
                        editPolicyModal.showModal({ policyUserRole: record })
                      }
                    }}
                    disabled={!isCustomPolicyRow(record)}
                  >
                    {t(`権限の編集`)}
                  </Button>
                )
              },
            },
            createDestroyColumn(
              loading,
              setLoading,
              async (record: PolicyRow) => {
                if (users.users.some((user) => user.role === record.name)) {
                  throw new Error(
                    t(`割り当て中のユーザーがいるため削除できません。他の権限に変更後に削除してください。`),
                  )
                }
                await deletePolicyUserRoleMutation({
                  variables: {
                    organizationId: getOrganizationIdFromPath(),
                    id: record.id,
                  },
                  refetchQueries: ['fetchPolicyUserRoles'],
                })
              },
              isCustomPolicyRow,
            ),
          ]}
        />
      </PageHeader>
      <PolicyCreateModal modal={createPolicyModal} />
      <PolicyEditModal modal={editPolicyModal} />
    </>
  )
}
